import { useRef, useState } from "react";
import * as XLSX from "xlsx";
import useNotification from "../../../shared/hooks/useNotification";
import { useApi } from "../../../shared/hooks/useApi";
import { auditDataRoutes } from "../constants/constants";
import {
  extractCbDataFromExcel,
  ExtractedAuditData,
  findSheetNameCaseInsensitive,
  REQUIRED_SHEETS,
  validateAuditFileName,
} from "../utils/excel-parser";
import {
  StructuredApiErrorResponse,
  ValidationTimelineStep,
} from "../types/audit-data.types";

const MAX_SIZE_MB = 25;

export const useAuditDataForm = () => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [parsing, setParsing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [extractedData, setExtractedData] = useState<ExtractedAuditData | null>(null);
  const [timelineSteps, setTimelineSteps] = useState<ValidationTimelineStep[]>([]);
  const [serverErrors, setServerErrors] = useState<StructuredApiErrorResponse | null>(null);
  const [legacyErrors, setLegacyErrors] = useState<string[]>([]);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const { notify } = useNotification();
  const { executePost } = useApi();

  const allTimelinePassed =
    timelineSteps.length === 4 && timelineSteps.every((step) => step.passed);

  const resetState = () => {
    setSelectedFile(null);
    setExtractedData(null);
    setTimelineSteps([]);
    setServerErrors(null);
    setLegacyErrors([]);
    setSuccessMsg(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const processFile = async (file: File) => {
    resetState();
    setSelectedFile(file);
    setParsing(true);

    const steps: ValidationTimelineStep[] = [
      { id: 1, label: "File format", passed: false },
      { id: 2, label: "Required Sheets", passed: false },
      { id: 3, label: "Required Fields", passed: false },
      { id: 4, label: "File Name Format", passed: false },
    ];

    // Step 1: File format check
    const isExcelFormat = !!file.name.match(/\.(xlsx|xls)$/i);
    const isUnderSizeLimit = file.size <= MAX_SIZE_MB * 1024 * 1024;

    if (!isExcelFormat) {
      steps[0].passed = false;
      steps[0].message = "Selected file is not an Excel file (.xlsx or .xls).";
      setTimelineSteps(steps);
      setParsing(false);
      return;
    }

    if (!isUnderSizeLimit) {
      steps[0].passed = false;
      steps[0].message = `File size exceeds maximum limit of ${MAX_SIZE_MB}MB.`;
      setTimelineSteps(steps);
      setParsing(false);
      return;
    }

    steps[0].passed = true;
    steps[0].message = "Valid .xlsx / .xls file format.";
    setTimelineSteps([...steps]);

    // Step 2 & 3: Parse Excel using xlsx library
    try {
      const buffer = await file.arrayBuffer();

      // Step 2: Validate Required Sheets
      const availableSheets = XLSX.read(buffer, { type: "array", bookSheets: true }).SheetNames;
      const missingSheets: string[] = REQUIRED_SHEETS.filter(
        (reqSheet) => !findSheetNameCaseInsensitive(availableSheets, reqSheet)
      );
      const refSheetName = findSheetNameCaseInsensitive(availableSheets, "Ref");

      if (missingSheets.length > 0 || !refSheetName) {
        const allMissing: string[] = [...missingSheets];
        if (!refSheetName && !allMissing.includes("Ref")) {
          allMissing.push("Ref");
        }
        steps[1].passed = false;
        steps[1].message = `Missing required sheet(s): ${allMissing.join(", ")}`;
        steps[1].details = allMissing.map((s: string) => `Missing sheet: "${s}"`);
        setTimelineSteps([...steps]);
        setParsing(false);
        return;
      }

      // Step 2 Passed
      steps[1].passed = true;
      steps[1].message = `All 7 required sheets present (${REQUIRED_SHEETS.length}/${REQUIRED_SHEETS.length}).`;
      steps[1].details = [];
      setTimelineSteps([...steps]);

      // Step 3: Fields extraction check
      const extracted = extractCbDataFromExcel(buffer);
      setExtractedData(extracted);

      steps[2].passed = true;
      steps[2].message = `Extracted CB Code '${extracted.cbCode}', TE-ID '${extracted.teId}', Dates (${extracted.auditStartDate} to ${extracted.auditEndDate})`;
      steps[2].details = [];
      setTimelineSteps([...steps]);

      // Step 4: File name validation
      const nameValidation = validateAuditFileName(file.name, extracted);
      if (!nameValidation.valid) {
        steps[3].passed = false;
        steps[3].message = nameValidation.error;
        if (nameValidation.expectedPatterns) {
          steps[3].details = nameValidation.expectedPatterns.map(
            (p) => `Expected Pattern: ${p}.xlsx`
          );
        }
        setTimelineSteps([...steps]);
        setParsing(false);
        return;
      }

      steps[3].passed = true;
      steps[3].message = `File name matches required pattern format.`;
      setTimelineSteps([...steps]);
    } catch (err: any) {
      const errMsg = err?.message || "Failed to parse Excel file.";
      if (errMsg.startsWith("FIELD_ERRORS::")) {
        const jsonStr = errMsg.replace("FIELD_ERRORS::", "");
        try {
          const fieldErrList: string[] = JSON.parse(jsonStr);
          steps[2].passed = false;
          steps[2].message = `Field validation failed (${fieldErrList.length} issue(s) found):`;
          steps[2].details = fieldErrList;
        } catch {
          steps[2].passed = false;
          steps[2].message = errMsg;
        }
      } else if (errMsg.toLowerCase().includes("missing required worksheet")) {
        steps[1].passed = false;
        steps[1].message = errMsg;
        const match = errMsg.match(/Missing required worksheet\(s?\):\s*(.+)/i);
        if (match) {
          const list = match[1].split(",").map((s: string) => s.trim());
          steps[1].details = list.map((s: string) => `Missing sheet: "${s}"`);
        }
      } else if (!steps[1].passed) {
        steps[1].passed = false;
        steps[1].message = errMsg;
      } else {
        steps[2].passed = false;
        steps[2].message = errMsg;
      }
      setTimelineSteps([...steps]);
    } finally {
      setParsing(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) processFile(file);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (!uploading && !parsing) setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    if (uploading || parsing) return;
    const file = e.dataTransfer.files?.[0];
    if (file) processFile(file);
  };

  const handleUpload = async () => {
    if (!selectedFile || !allTimelinePassed) {
      notify("Please ensure all validation checks have passed before uploading.", "error");
      return;
    }

    setUploading(true);
    setServerErrors(null);
    setLegacyErrors([]);
    setSuccessMsg(null);

    try {
      const fileBytes = await selectedFile.arrayBuffer();
      const url = `${auditDataRoutes.UPLOAD_EXCEL}?filename=${encodeURIComponent(selectedFile.name)}`;

      const response = await executePost(url, fileBytes, {
        headers: { "Content-Type": "application/octet-stream" },
      });

      if (response?.status === 200 || response?.status === 201) {
        const okMsg =
          (response?.data as any)?.message ||
          (response?.data as any)?.response ||
          "Audit Data Excel file uploaded successfully!";
        notify(okMsg, "success");
        setSuccessMsg(okMsg);
        return;
      }

      if (response?.data?.details && Array.isArray(response.data.details)) {
        setServerErrors(response.data);
      } else if (response?.data?.message) {
        setLegacyErrors([response.data.message]);
      } else {
        setLegacyErrors(["Upload failed. Please review the file and try again."]);
      }
      notify("Excel Validation failed. See error details below.", "error");
    } catch (err: any) {
      const errData = err?.response?.data;
      if (errData?.details && Array.isArray(errData.details)) {
        setServerErrors(errData);
      } else if (errData?.message) {
        setLegacyErrors([errData.message]);
      } else if (Array.isArray(errData?.errors)) {
        setLegacyErrors(errData.errors);
      } else {
        setLegacyErrors([err?.message || "Network error. Could not connect to upload service."]);
      }
      notify("Upload failed with errors.", "error");
    } finally {
      setUploading(false);
    }
  };

  return {
    selectedFile,
    parsing,
    uploading,
    extractedData,
    timelineSteps,
    serverErrors,
    legacyErrors,
    successMsg,
    isDragging,
    fileInputRef,
    allTimelinePassed,
    notify,
    setSuccessMsg,
    resetState,
    handleFileChange,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    handleUpload,
  };
};
